// src/utils/billingTotals.js

// Reuse your integer-cents infra from totals.js
import { toCents, fromCents } from './totals';

// Re-export so other files can import from here
export { toCents, fromCents };

// Safe numeric picker
const pickNum = (...vals) => {
  for (const v of vals) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

export const lineExtendedCents = (quantity, unitCost) => {
  const qty = Number(quantity);
  const unit = Number(unitCost);
  if (!Number.isFinite(qty) || !Number.isFinite(unit)) return 0;
  return Math.round(qty * unit * 100);
};

const hasMoneyValue = (value) => value !== null && value !== undefined && value !== '';

const lineSubtotalCents = (row = {}) => {
  if (hasMoneyValue(row?._poAuthoritativeSubTotal) && row?._poPrecisionEdited !== true) {
    return toCents(row._poAuthoritativeSubTotal);
  }
  if (hasMoneyValue(row?.subTotal)) {
    return toCents(row.subTotal);
  }
  return lineExtendedCents(row?.quantity ?? row?.Quantity, row?.unitCost ?? row?.UnitCost);
};

// Parse "GST 5% + PST 7%" OR "5%" OR "5 + 7" OR 5 (number) → [5, 7]
const parsePercentList = (src) => {
  if (src == null || src === '') return [];
  if (typeof src === 'number') return Number.isFinite(src) ? [src] : [];
  const s = String(src);

  // First grab any explicit “xx.xx%”
  const matches = s.match(/([\d.]+)\s*%/g);
  if (matches && matches.length) {
    return matches
      .map(m => Number(m.replace('%', '').trim()))
      .filter(n => Number.isFinite(n) && n >= 0);
  }

  // raw numeric ids (e.g. "11") should not be treated as 11%
  if (/^\d+(\.\d+)?$/.test(s.trim())) return [];

  // Fallback: split on non-number punctuation and read decimals (e.g. "5 + 7")
  return s
    .split(/[^0-9.]+/g)
    .map(x => Number(x))
    .filter(n => Number.isFinite(n) && n >= 0);
};

const resolvePercentList = (src, taxOptions = []) => {
  if (src == null || src === '') return [];
  const raw = String(src).trim();
  if (!raw) return [];
  const asNum = Number(raw);
  const byId = Array.isArray(taxOptions)
    ? taxOptions.find((t) => {
        const idRaw = String(t?.id ?? '').trim();
        if (idRaw === raw) return true;
        const idNum = Number(idRaw);
        return Number.isFinite(asNum) && Number.isFinite(idNum) && idNum === asNum;
      })
    : null;
  if (byId) {
    return parsePercentList(byId?.name || byId?.label || byId?.value || '');
  }
  return parsePercentList(src);
};

// Per-line tax = round(extendedCents * rate) then sum (consistent with cart)
const lineTaxFromPercentsC = (extendedLineCents, percents = []) => {
  let taxC = 0;
  for (const p of percents) {
    const r = Number(p) / 100;
    if (Number.isFinite(r) && r >= 0) {
      taxC += Math.round(extendedLineCents * r);
    }
  }
  return taxC;
};

/**
 * computeBillTotalsCents
 * bill shape:
 * {
 *   billingItems: [{ quantity, unitCost, tax, taxAmount } ...],
 *   shipping, misc, adjustment (optional numbers)
 * }
 * Rules:
 * - Subtotal: sum(qty * unitCost) in cents.
 * - Tax: prefer numeric taxAmount when present; else parse multi-tax % from `tax`.
 * - Shipping/Misc/Adjustment: added verbatim (can be negative for credits)
 * - No retail/margin/lock/pass-through logic here (bill = cost reality).
 */
export const computeBillTotalsCents = (bill = {}, { taxOptions = [] } = {}) => {
  const items = Array.isArray(bill?.billingItems) ? bill.billingItems
              : Array.isArray(bill?.items) ? bill.items
              : [];

  let subTotalCents = 0;
  let taxCents      = 0;

  for (const it of items) {
    const extC  = lineSubtotalCents(it);
    subTotalCents += extC;

    // Prefer explicit taxAmount override if provided
    const hasExplicitTaxAmount = it?.taxAmount !== null && it?.taxAmount !== undefined && it?.taxAmount !== '';
    const taxAmtC = toCents(it?.taxAmount);
    if (hasExplicitTaxAmount) {
      taxCents += taxAmtC;
    } else {
      const pcts = resolvePercentList(it?.tax, taxOptions);
      taxCents  += lineTaxFromPercentsC(extC, pcts);
    }
  }

  const shippingCents   = toCents(pickNum(bill?.shipping, bill?.shippingCost));
  const miscCents       = toCents(pickNum(bill?.misc, bill?.extraFees));
  const adjustmentCents = toCents(pickNum(bill?.adjustment)); // optional, can be negative

  const totalCents = subTotalCents + taxCents + shippingCents + miscCents + adjustmentCents;

  return {
    subTotalCents,
    taxCents,
    shippingCents,
    miscCents,
    adjustmentCents,
    totalCents,
  };
};

// Sum actuals across many bills (e.g., a PO group)
// Persisted bills may have a bill-level tax override. Prefer their saved total;
// only calculate from lines for unsaved/legacy objects without header totals.
export const sumBillsActualCents = (bills = []) =>
  bills.reduce((acc, bill) => {
    const savedAmount = Number(bill?.amount);
    if (Number.isFinite(savedAmount)) return acc + toCents(savedAmount);
    const savedSubtotal = Number(bill?.subTotal);
    const savedTax = Number(bill?.totalTax);
    if (Number.isFinite(savedSubtotal) && Number.isFinite(savedTax)) {
      return acc + toCents(savedSubtotal) + toCents(savedTax);
    }
    return acc + computeBillTotalsCents(bill).totalCents;
  }, 0);

// Helper for display
export const formatCAD = (n) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' })
    .format(fromCents(toCents(n)));
